/*
(function() {
  console.group('UTM Tracking Debug Report');
  console.log('URL UTM ID:', window.UTMTracking.captureUTMId());
  console.log('Has Consent:', window.UTMTracking.hasConsent());
  console.log('Stored UTM ID:', window.UTMTracking.getStoredUTMId());
  console.log('Pending UTM ID:', window.UTMTracking.getPendingUTMId());
  console.log('UTM ID for Cart:', window.UTMTracking.getUTMIdForCart());
  fetch('/cart.js').then(r => r.json()).then(cart => {
    console.log('Cart Attributes:', cart.attributes);
    console.log('Cart UTM ID:', cart.attributes?.utm_id);
  });
  console.groupEnd();
})();
*/
import { ThemeEvents } from '@theme/events';

const UTM_ID_KEY = 'utm_id';
const DEFAULT_UTM_ID = '';
const STORAGE_KEY = 'utm_id';
const STORAGE_EXPIRY_KEY = 'utm_id_expiry';
const SESSION_STORAGE_KEY = 'utm_id_pending';
const CUSTOMER_PRIVACY_API_VERSION = '0.1';
const CART_UPDATE_DELAY_MS = 100;
const MAX_UTM_ID_LENGTH = 255;
const UTM_STORAGE_DURATION_DAYS = 30;

let updateCartAttributesTimeout = null;

function log() {
  console.group('UTM Tracking Debug Report');
  console.log('URL UTM ID:', window.UTMTracking.captureUTMId());
  console.log('Has Consent:', window.UTMTracking.hasConsent());
  console.log('Stored UTM ID:', window.UTMTracking.getStoredUTMId());
  console.log('Pending UTM ID:', window.UTMTracking.getPendingUTMId());
  console.log('UTM ID for Cart:', window.UTMTracking.getUTMIdForCart());
  fetch('/cart.js').then(r => r.json()).then(cart => {
    console.log('Cart Attributes:', cart.attributes);
    console.log('Cart UTM ID:', cart.attributes?.utm_id);
  });
  console.groupEnd();
}

function hasConsent() {
  try {
    if (!window.Shopify?.customerPrivacy) {
      return false;
    }
    return window.Shopify.customerPrivacy.marketingAllowed() || 
           window.Shopify.customerPrivacy.analyticsProcessingAllowed();
  } catch {
    return false;
  }
}

function captureUTMId() {
  try {
    const utmId = new URLSearchParams(window.location.search).get(UTM_ID_KEY);
    return utmId ? utmId.trim() : null;
  } catch {
    return null;
  }
}

function isValidUTMId(utmId) {
  return utmId && typeof utmId === 'string' && utmId.trim().length > 0 && utmId.trim().length <= MAX_UTM_ID_LENGTH;
}

function storePendingUTMId(utmId) {
  if (!isValidUTMId(utmId)) return;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, utmId);
  } catch {}
}

function getPendingUTMId() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearPendingUTMId() {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {}
}

function storeUTMId(utmId) {
  if (!isValidUTMId(utmId)) return;

  if (!hasConsent()) {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_EXPIRY_KEY);
      clearPendingUTMId();
    } catch {}
    return;
  }

  try {
    const expiryMs = Date.now() + UTM_STORAGE_DURATION_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, utmId);
    localStorage.setItem(STORAGE_EXPIRY_KEY, expiryMs.toString());
    clearPendingUTMId();
  } catch {}
}

function getStoredUTMId() {
  try {
    const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
    if (expiry && Date.now() > parseInt(expiry, 10)) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_EXPIRY_KEY);
      return null;
    }
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function getUTMIdForCart() {
  if (!hasConsent()) {
    return null;
  }
  const stored = getStoredUTMId();
  if (stored) return stored;
  return DEFAULT_UTM_ID;
}

function getCartAttributes() {
  return {
    attributes: {
      [UTM_ID_KEY]: getUTMIdForCart()
    }
  };
}

async function updateCartAttributes() {
  if (!hasConsent()) {
    return Promise.resolve(null);
  }

  if (updateCartAttributesTimeout) {
    clearTimeout(updateCartAttributesTimeout);
  }

  return new Promise((resolve) => {
    updateCartAttributesTimeout = setTimeout(async () => {
      try {
        const cartAttributes = getCartAttributes();
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartAttributes),
        });

        if (!response.ok) {
          resolve(null);
          return;
        }

        resolve(await response.json());
      } catch {
        resolve(null);
      }
    }, CART_UPDATE_DELAY_MS);
  });
}

function processUTMId(utmId) {
  if (!isValidUTMId(utmId)) return;
  if (hasConsent()) {
    storeUTMId(utmId);
  } else {
    // Don't store pending UTM ID if consent is not given
    clearPendingUTMId();
  }
}

function initUTMTracking() {
  const utmId = captureUTMId();
  
  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: CUSTOMER_PRIVACY_API_VERSION }],
      (error) => {
        if (!error && utmId) {
          processUTMId(utmId);
        }
        // Only update cart if consent is given
        if (hasConsent()) {
          updateCartAttributes();
        }
      }
    );
  } else if (window.Shopify?.customerPrivacy) {
    if (utmId) {
      processUTMId(utmId);
    }
    // Only update cart if consent is given
    if (hasConsent()) {
      updateCartAttributes();
    }
  } else {
    // No consent API available, don't track
    if (hasConsent()) {
      updateCartAttributes();
    }
  }
}

function handleCartAdd() {
  updateCartAttributes();
}

function handleCartUpdate() {
  updateCartAttributes();
}

function handleVisitorConsentCollected() {
  
  //console.log('handleVisitorConsentCollected');
  
  const pendingUtmId = getPendingUTMId();
  const storedUtmId = getStoredUTMId();
  const currentUrlUtmId = captureUTMId();

  if (!hasConsent()) {

    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_EXPIRY_KEY);
      clearPendingUTMId();
    } catch {}
    // Don't update cart if consent is not given
    //log();
    return;
  } else {

    const utmIdToStore = currentUrlUtmId || pendingUtmId || storedUtmId;
    if (utmIdToStore && isValidUTMId(utmIdToStore)) {
      storeUTMId(utmIdToStore);
    }
  }

  //log();

  // Only update cart if consent is given
  updateCartAttributes();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUTMTracking);
} else {
  initUTMTracking();
}

document.addEventListener(ThemeEvents.cartAdd, handleCartAdd);
document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

window.addEventListener("CookiebotOnConsentReady", function () {
  setTimeout(handleVisitorConsentCollected,1000);
  //handleVisitorConsentCollected();
});

//document.addEventListener('visitorConsentCollected', handleVisitorConsentCollected);

if (window.Shopify?.customerPrivacy) {
  window.Shopify.customerPrivacy.on?.('visitorConsentCollected', handleVisitorConsentCollected);
}

if (typeof window !== 'undefined') {
  window.UTMTracking = {
    captureUTMId,
    getStoredUTMId,
    getPendingUTMId,
    getUTMIdForCart,
    updateCartAttributes,
    hasConsent,
    processUTMId,
  };
}
